import { useState } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';

/**
 * Props:
 *   firm       – the firm object (needs .id, .name, .paymentCustomerId)
 *   onDeleted  – callback invoked after successful deletion
 */
const FirmDangerZone = ({ firm, onDeleted }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteStripeCustomer, setDeleteStripeCustomer] = useState(true);

  const isDeleteEnabled = confirmText.trim().toLowerCase() === 'delete';

  const openDialog = () => {
    setConfirmText('');
    setError(null);
    setDeleteStripeCustomer(true);
    setShowDialog(true);
  };

  const closeDialog = () => {
    if (deleting) return;
    setShowDialog(false);
    setConfirmText('');
    setError(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      await callUIAPI(
        `bo/firms/${firm.id}?deleteStripeCustomer=${deleteStripeCustomer}`,
        null,
        apiConstants.METHOD_DELETE,
        true,
      );
      onDeleted();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete firm.');
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="firm-danger-zone">
        <div>
          <h3 className="firm-danger-title">Delete this firm</h3>
          <p className="firm-danger-description">
            Permanently removes the firm and all associated data. This action cannot be undone.
          </p>
        </div>
        <button className="firm-btn-delete" onClick={openDialog}>Delete firm</button>
      </div>

      {showDialog && (
        <div className="firm-overlay" onClick={closeDialog}>
          <div className="firm-dialog" onClick={(e) => e.stopPropagation()}>
            {deleting ? (
              <div className="firm-deleting-container">
                <div className="firm-spinner" />
                <p className="firm-deleting-text">Deleting firm and all associated data…</p>
                <p className="firm-deleting-subtext">This may take a moment.</p>
              </div>
            ) : (
              <>
                <div className="firm-dialog-icon">⚠</div>
                <h3 className="firm-dialog-title">Delete &ldquo;{firm.name}&rdquo;?</h3>
                <p className="firm-dialog-body">
                  This will <strong>permanently delete</strong> the firm and <strong>all</strong> data
                  associated with it — users, content, companies, campaigns, subscriptions, and
                  everything else. This cannot be reversed.
                </p>
                {firm.paymentCustomerId && (
                  <label className="firm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={deleteStripeCustomer}
                      onChange={(e) => setDeleteStripeCustomer(e.target.checked)}
                    />
                    <span>Also delete Stripe customer (<strong>{firm.paymentCustomerId}</strong>)</span>
                  </label>
                )}
                {error && <p className="firm-dialog-error">{error}</p>}
                <label className="firm-confirm-label">Type <strong>delete</strong> to confirm</label>
                <input
                  className="firm-confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="delete"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteEnabled) handleDelete();
                    if (e.key === 'Escape') closeDialog();
                  }}
                />
                <div className="firm-dialog-actions">
                  <button className="firm-btn firm-btn-secondary" onClick={closeDialog}>Cancel</button>
                  <button className="firm-btn-confirm-delete" disabled={!isDeleteEnabled} onClick={handleDelete}>
                    Delete firm permanently
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FirmDangerZone;